import { FileText, Image as ImageIcon, Loader2, Volume2, VolumeX } from 'lucide-react';
import { useState } from 'react';
import type { AgentTemplate } from '../../../types/agent';
import { AgentMessageMarkdown } from '../AgentMessageMarkdown';
import { useTypewriterReveal } from '../../../hooks/useTypewriterReveal';
import type { ChatMessage } from './publicAgentHelpers';

export function PublicAgentCoverMedia({ template }: { template: AgentTemplate }) {
  const url = template.cover_media_url?.trim();
  if (!url) return null;
  const isVideo = template.cover_media_type === 'video';
  const [muted, setMuted] = useState(true);

  return (
    <div className="w-full overflow-hidden rounded-2xl border border-subtle/80 bg-elevated/50 aspect-video shadow-lg shadow-black/20">
      {isVideo ? (
        <div className="relative h-full w-full">
          <video
            src={url}
            className="h-full w-full object-cover"
            autoPlay
            loop
            muted={muted}
            playsInline
            controls={false}
          />
          <button
            type="button"
            onClick={() => setMuted((m) => !m)}
            className="absolute bottom-2 right-2 inline-flex items-center gap-1.5 rounded-lg border border-subtle/80 bg-base/90 px-2.5 py-1.5 text-[12px] font-medium text-th-primary shadow-md backdrop-blur-sm transition hover:bg-elevated/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
            aria-label={muted ? 'Unmute video' : 'Mute video'}
          >
            {muted ? (
              <>
                <VolumeX className="h-4 w-4 shrink-0" aria-hidden />
                Unmute
              </>
            ) : (
              <Volume2 className="h-4 w-4 shrink-0" aria-hidden />
            )}
          </button>
        </div>
      ) : (
        <img src={url} alt="" className="h-full w-full object-cover" loading="lazy" decoding="async" />
      )}
    </div>
  );
}

export function UserMessageBubble({ message }: { message: Extract<ChatMessage, { role: 'user' }> }) {
  const { content, attachments } = message;
  const hasAtt = attachments && attachments.length > 0;
  return (
    <div className="min-w-0">
      {hasAtt ? (
        <div className="mb-2 flex flex-wrap gap-2">
          {attachments!.map((a, i) =>
            a.kind === 'image' ? (
              a.url ? (
                <div key={`${i}-${a.filename}`} className="flex max-w-[200px] flex-col gap-1">
                  <img
                    src={a.url}
                    alt=""
                    className="max-h-40 w-full rounded-xl border border-subtle/50 object-cover"
                  />
                  <span className="truncate text-center text-[11px] text-th-secondary" title={a.filename}>
                    {a.filename}
                  </span>
                </div>
              ) : (
                <span
                  key={`${i}-${a.filename}`}
                  className="inline-flex max-w-[min(180px,50vw)] items-center gap-1.5 rounded-full border border-brand/30 bg-brand/10 px-2.5 py-1.5 text-[12px] font-medium text-th-primary"
                  title={a.filename}
                >
                  <ImageIcon className="h-3.5 w-3.5 shrink-0 text-brand" aria-hidden />
                  <span className="truncate">{a.filename}</span>
                </span>
              )
            ) : (
              <span
                key={`${i}-${a.filename}`}
                className="inline-flex max-w-[min(180px,50vw)] items-center gap-1.5 rounded-full border border-brand/30 bg-brand/10 px-2.5 py-1.5 text-[12px] font-medium text-th-primary"
                title={a.filename}
              >
                <FileText className="h-3.5 w-3.5 shrink-0 text-brand" aria-hidden />
                <span className="truncate">{a.filename}</span>
              </span>
            )
          )}
        </div>
      ) : null}
      {content ? <p className="whitespace-pre-wrap break-words">{content}</p> : null}
    </div>
  );
}

export function AssistantMessageBubble({
  content,
  isLastAssistant,
  sending,
  activityHint,
}: {
  content: string;
  isLastAssistant: boolean;
  sending: boolean;
  /** Server tool phase (e.g. Tavily web search) — shown before first token. */
  activityHint?: string | null;
}) {
  const visible = useTypewriterReveal(content, {
    enabled: isLastAssistant,
    isStreaming: sending,
  });
  const catchingUp = isLastAssistant && visible.length < content.length;
  const showStreamCursor = isLastAssistant && (catchingUp || sending);
  const usePlainWhileStreaming = isLastAssistant && (sending || catchingUp);

  if (content.trim() === '(no text)') {
    return <p className="text-sm text-th-tertiary">No response was generated. Try again or rephrase.</p>;
  }

  if (!content.trim() && sending && isLastAssistant) {
    const label = activityHint?.trim() || 'Thinking…';
    return (
      <div className="flex min-w-0 items-center gap-2 text-[13px] leading-snug text-th-tertiary">
        <span className="sr-only">Generating response</span>
        <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-th-primary/40" aria-hidden />
        <span className="min-w-0 truncate">{label}</span>
      </div>
    );
  }

  if (!content) return null;

  if (usePlainWhileStreaming) {
    return (
      <div className="min-w-0 whitespace-pre-wrap break-words font-chat text-[15px] leading-relaxed text-th-primary transition-[color] duration-150">
        {activityHint && isLastAssistant && sending ? (
          <p className="mb-2 text-xs font-medium text-accent/90 transition-opacity duration-200">
            {activityHint}
          </p>
        ) : null}
        {visible}
        {showStreamCursor ? (
          <span
            className="ml-px inline-block h-[1.05em] w-0.5 min-h-[1em] animate-pulse rounded-sm bg-th-primary/75 align-baseline transition-opacity duration-200"
            aria-hidden
          />
        ) : null}
      </div>
    );
  }

  return <AgentMessageMarkdown content={content} streamCursor={false} />;
}
